import React, { useState } from 'react';
import {
  Page,
  CompactTable,
  useFetch,
  useFilters,
  usePageQuery,
  PermissionGuard,
  useUserinfo,
  RemoteRequest,
  openNotificationWithIcon,
} from '@mss/mss-component-library';
import PageSidebar from 'jsx/common/FilterSidebar';
import Breadcrumbs from '../../../common/Breadcrumbs';
import columns from './columns';
import { permissionSet } from '../../../authorization/util/permissionSets';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import RemoteRequestViewModal from '../../components/RemoteRequestViewModal';
import DirectRemoteConnectModal from 'jsx/asset/components/DirectRemoteConnectModal';
import { TFunction } from 'i18next';

const filterKeys: any[] = ['asset_name', 'user_name', 'status_name'];
const searchKeys: any[] = [
  'asset_name',
  'user_name',
  'status_name',
  'profile_name',
  'profile_type',
];

const customOptionLabel = (t: TFunction) => (key: string, value: any) => {
  if (key === 'status') {
    switch (value) {
      case 'awaiting_approval':
        return t('Awaiting approval');
      case 'wait_for_start_date':
        return t('Future approved');
      case 'allow':
        return t('Approved');
      case 'denied':
        return t('Denied');
      case 'revoked':
        return t('Revoked');
      case 'expired':
        return t('Expired');
      case 'expired_unapproved':
        return t('Expired approval');
      default:
        return value;
    }
  }
  return value;
};

const customHeadingLabel = (t: TFunction) => (key: string) => {
  if (key === 'user_name') return t('User');
  if (key === 'asset') return t('Target Asset');
  if (key === 'status_name') return t('Status');
  if (key === 'asset_name') return t('Target Asset');
  return key;
};

const PAGE_SIZE = 10;

export default function List() {
  const [, , , hasAnyPermission] = useUserinfo();
  const [sessionRequests, setSessionsRequests] = useState<RemoteRequest[]>([]);
  const [connectModal, openConnectModal] = useState<any | null>(null);
  const [t] = useTranslation(['remoteAccess', 'common', 'profile', 'asset']);
  const [modal, setModal] = useState<RemoteRequest | null>(null);

  const [rawRequests] = useFetch({
    url: '/api/remote-request/',
    defaultValue: [],
    initialFetch: hasAnyPermission(permissionSet.remoteAny),
    onSucces: async ({ data }) => {
      try {
        setSessionsRequests(
          data.map((x: any) => ({
            ...x,
            asset_name: x.asset_object.name,
            user_name: x.request_user_object.name,
            profile_name: x.profile_object.name,
            status_name: customOptionLabel(t)('status', x.status),
            profile_type: `${x.profile_object.protocol.toUpperCase()} - ${
              x.profile_object.name
            }`,
          }))
        );
      } catch {
        openNotificationWithIcon(
          notification.error,
          t('Failed fetch assets'),
          t(
            'Due to technical issues assets could not be retrieved. Therefore you will only see the id of the assets. Please try again later.'
          )
        );
      }
    },
  });

  const isFetching = rawRequests.loading;

  const [availableFilters, toggleFilter, filteredData, search] = useFilters(
    sessionRequests,
    filterKeys,
    searchKeys,
    customOptionLabel(t),
    customHeadingLabel(t)
  );
  const [page, goToPage] = usePageQuery(filteredData.length / PAGE_SIZE);

  return (
    <PermissionGuard any codename={permissionSet.remoteAny}>
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        main={
          <PermissionGuard any codename={permissionSet.remoteAny}>
            <CompactTable
              bordered={false}
              dataSource={filteredData}
              loading={isFetching}
              columns={columns(t, openConnectModal)}
              rowKey={'id'}
              onRow={(record: any) => ({
                onClick: () => setModal(record),
                style: { cursor: 'default' },
              })}
              pagination={{
                current: page,
                showQuickJumper: true,
                showSizeChanger: true,
                total: filteredData.length,
                showTotal: (total: number, range: any[]) =>
                  t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                    range1: range[0],
                    range2: range[1],
                    TOTAL: total,
                  }),
              }}
              onChange={(pagination: any) => {
                if (page !== pagination.current) goToPage(pagination.current);
              }}
            />
            <RemoteRequestViewModal
              request={modal}
              closeModal={() => {
                setModal(null);
              }}
              visible={!!modal}
            />
          </PermissionGuard>
        }
        sidebar={
          <PermissionGuard any codename={permissionSet.remoteAny}>
            <PageSidebar
              availableFilters={availableFilters}
              toggleFilter={toggleFilter}
              search={search}
            />
          </PermissionGuard>
        }
      />
      <DirectRemoteConnectModal
        closeModal={() => openConnectModal(null)}
        remoteRequest={connectModal}
        visible={!!connectModal}
      />
    </PermissionGuard>
  );
}
